// ============================================================================
// Agreements (AGR-01) — turn the mirrored ServAgr + AgrTask rows into a clean
// view for the Customer 360 Agreements box: status, dates, and the included
// tune-ups with their "X of Y" progress (the first cut of the Benefits Ledger).
// Pure functions, no AI. The living plan-visit engine (defer/substitute/
// carry-over) is AGR-03; this is the read view over the ESC data.
// ============================================================================

export interface AgreementRow {
  custno: string;
  locno: string;
  agrmt_no: string;
  agrmt_type: string | null;   // "1YR"
  contr_per: string | null;    // "1 Year"
  contr_amt: number | null;
  dept: string | null;
  orig_contr: string | null;
  beg_date: string | null;
  renew_date: string | null;
  expire_date: string | null;
  last_srvc: string | null;
  next_post_date: string | null;
  sa_inactive: number | null;  // 0 active, -1 inactive
  notes: string | null;
}

export interface AgrTaskRow {
  master_task: string;
  counter: string;
  task: string | null;
  time_min: string | null;
  long_desc: string | null;
  sub_total: number | null;
}

export interface AgrPartRow {
  master_task: string;
  part: string;
  quantity: number | null;
}

export type AgrStatus = "active" | "expiring" | "expired" | "inactive";

export interface AgrTaskView {
  label: string;         // "Furnace tune-up"
  rawDesc: string;       // verbatim ESC desc
  used: number | null;   // X in "X of Y"
  total: number | null;  // Y
  timeMin: number | null;
}

export interface AgreementView extends AgreementRow {
  status: AgrStatus;
  statusLabel: string;
  daysToExpire: number | null;
  planName: string;          // "1-Year Plan"
  tasks: AgrTaskView[];
  parts: AgrPartRow[];
}

const todayISO = new Date().toISOString().slice(0, 10);

function daysBetween(fromISO: string, toISO: string): number {
  const a = new Date(fromISO + "T00:00:00").getTime();
  const b = new Date(toISO + "T00:00:00").getTime();
  return Math.round((b - a) / 86400000);
}

function planName(a: AgreementRow): string {
  const p = (a.contr_per ?? "").trim();
  if (p) return p.replace(/\bYear\b/i, "Year").replace(/^(\d+)\s*Year$/i, "$1-Year") + " Plan";
  const t = (a.agrmt_type ?? "").toUpperCase();
  if (/^\d+YR$/.test(t)) return `${t.replace("YR", "")}-Year Plan`;
  return t ? `${t} Plan` : "Service Agreement";
}

/**
 * detectCheckType — classify an ESC check description / dispatch note by the
 * EARLIEST matching check phrase, not by a fixed test order. The fixed order
 * misread Sedgwick's standard phrasing: "1 of 6 PLUS AC CK,, ACC AT FRN CK"
 * ("accessories at furnace check") contains BOTH "AC CK" and "FRN CK", and
 * furnace-first rules labeled the A/C visit a furnace visit — which cascaded
 * into wrong covered-equipment defaults and wrong last-done ledger dates
 * (found on Mary Gannon 0010349, Seth 2026-08-18). The PRIMARY check always
 * leads the note, so earliest-match wins. Shared by the agreement task
 * parser, the visit-history classifier, and the membership card.
 */
export function detectCheckType(
  text: string,
  opts?: { strict?: boolean },
): string | null {
  const h = text.toUpperCase();
  const candidates: { label: string; idx: number }[] = [];
  const probe = (re: RegExp, label: string) => {
    const m = re.exec(h);
    if (m) candidates.push({ label, idx: m.index });
  };
  // Tier 1 — explicit CHECK phrases. Safe on free-text dispatch notes: a
  // "No Cool" repair never matches these, so repairs stay unlabeled (the
  // strict mode parseVisitType needs — a billed repair must not count as a
  // covered check).
  probe(/\bA\/?C\s*CK|COOL(ING)?\s*C(K|HECK)/, "A/C tune-up");
  probe(/\bFRN\s*CK|FURN(ACE)?\s*C(K|HECK)|HEAT\s*C(K|HECK)/, "Furnace tune-up");
  probe(/BOILER\s*C(K|HECK)/, "Boiler tune-up");
  probe(/PLUMB(ING)?\s*C(K|HECK)/, "Plumbing check");
  probe(/MINI.?SPLIT\s*C(K|HECK)|\bMINI\s*CK/, "Mini-split tune-up");
  if (candidates.length === 0 && !opts?.strict) {
    // Tier 2 — bare keywords, for agreement TASK lines (which are check
    // definitions by construction, so looser matching is safe there).
    probe(/\bA\/?C\b|COOL/, "A/C tune-up");
    probe(/\bFRN\b|FURNACE|\bHEAT\b/, "Furnace tune-up");
    probe(/BOILER/, "Boiler tune-up");
    probe(/PLUMB/, "Plumbing check");
    probe(/\bMINI/, "Mini-split tune-up");
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.idx - b.idx);
  return candidates[0].label;
}

// "2 of 6 PLUS FRN CK, HUMI, APR35" -> label "Furnace tune-up", used 2, total 6
function parseTask(t: AgrTaskRow): AgrTaskView {
  const raw = (t.long_desc ?? "").replace(/\s+/g, " ").trim();
  const h = raw.toUpperCase();
  let used: number | null = null;
  let total: number | null = null;
  const m = h.match(/(\d+)\s+OF\s+(\d+)/);
  if (m) { used = parseInt(m[1], 10); total = parseInt(m[2], 10); }

  const label = detectCheckType(h) ?? "Tune-up";

  const tmin = t.time_min ? parseInt(t.time_min, 10) : null;
  return { label, rawDesc: raw, used, total, timeMin: Number.isFinite(tmin as number) ? (tmin as number) : null };
}

export function buildAgreementView(a: AgreementRow, tasks: AgrTaskRow[], parts: AgrPartRow[]): AgreementView {
  const expired = !!a.expire_date && a.expire_date < todayISO;
  const days = a.expire_date ? daysBetween(todayISO, a.expire_date) : null;
  let status: AgrStatus;
  if (a.sa_inactive !== 0 && a.sa_inactive !== null) status = "inactive";
  else if (expired) status = "expired";
  else if (days !== null && days <= 60) status = "expiring";
  else status = "active";
  const statusLabel =
    status === "active" ? "Active" :
    status === "expiring" ? `Expiring${days !== null ? ` · ${days}d` : ""}` :
    status === "expired" ? "Expired" : "Inactive";

  return {
    ...a,
    status,
    statusLabel,
    daysToExpire: days,
    planName: planName(a),
    tasks: tasks.map(parseTask),
    parts,
  };
}
